import { useEffect, useState } from 'react';
import { Building2, Zap, Crown, Sparkles } from 'lucide-react';

// Static visual metadata that the backend doesn't (and shouldn't) own. Covers the full canonical ladder.
const TIER_META = {
  starter: { icon: Building2, color: 'bg-blue-100 text-blue-800' },
  growth: { icon: Zap, color: 'bg-green-100 text-green-800' },
  professional: { icon: Crown, color: 'bg-purple-100 text-purple-800' },
  scale: { icon: Sparkles, color: 'bg-indigo-100 text-indigo-800' },
  enterprise: { icon: Crown, color: 'bg-amber-100 text-amber-800' },
};

// NO hardcoded prices in the client. The canonical 5-plan ladder (Starter/Growth/Professional/Scale/
// Enterprise) is served by GET /billing/plans, sourced from commercial_plan_versions. The client renders
// only what the server returns (honest empty/loading state on failure — never an invented price). Enterprise
// has price=null (custom/on-request), which callers render as "Custom", never a fabricated amount.

const decorate = (tier) => {
  const staff = tier.limits?.staff;
  const sg = tier.limits?.storage_gb || 0;
  return {
    ...tier,
    ...(TIER_META[tier.id] || TIER_META.professional),
    limits: {
      users: staff === -1 ? 'Unlimited' : staff,
      engagements: tier.limits?.engagements === -1 ? 'Unlimited' : tier.limits?.engagements,
      storage: sg === -1 ? 'Unlimited' : (sg >= 1024 ? `${Math.round(sg / 1024)}TB` : `${sg}GB`),
      slots: tier.limits?.active_slots,
    },
  };
};

/**
 * useTiers — fetches subscription tiers from /api/billing/subscription/tiers (the server is the sole price
 * authority, backed by commercial_plan_versions). No hardcoded fallback prices: on failure the list is empty
 * and `error` is set, so callers render a loading/unavailable state rather than a stale invented price.
 */
export function useTiers() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/plans`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('plans fetch failed')))
      .then(data => {
        if (cancelled) return;
        const list = (data.plans || []).map(decorate);
        if (list.length) setTiers(list);
        setLoading(false);
      })
      .catch(e => {
        if (cancelled) return;
        setError(e);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Provide a map keyed by id for quick lookup
  const byId = tiers.reduce((acc, t) => { acc[t.id] = t; return acc; }, {});
  return { tiers, byId, loading, error };
}

export default useTiers;